import React from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

const PrivacyPage = () => {
  return (
    <>
      <SEO
        title="Privacy Policy | Snap Launch Studio"
        description="Snap Launch Studio's privacy policy outlines how we collect, use, and protect your information when you use our website and services."
        keywords="privacy policy, data protection, website privacy, information collection"
        canonicalUrl="https://www.snaplaunchstudio.com/privacy"
      />
      <PageHeader 
        title="Privacy Policy" 
      />
      
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="glass-card p-8">
              <div className="prose prose-lg max-w-none">
                <h1>Privacy Policy for Snap Launch Studio</h1>
                <p><strong>Effective Date: 6/6/2025</strong></p>
                
                <h2>1. Who We Are</h2>
                <p>
                  Snap Launch Studio is a website development and automation agency. We build high-converting websites, landing pages, and sales funnels, and we offer AI-driven marketing and CRM automation services using tools like Emergent.sh, Go High Level, and Zapier.
                </p>
                
                <h2>2. Information We Collect</h2>
                <p><strong>a. Publicly Available Business Information</strong></p>
                <p>
                  We may collect publicly accessible data from websites, social media profiles, and online business directories, including:
                </p>
                <ul>
                  <li>Business name, contact info, and location</li>
                  <li>Website URLs and content</li>
                  <li>Visual assets like logos or photos</li>
                  <li>Page layout, service descriptions, and branding</li>
                </ul>
                <p>
                  This information is used to generate sample website mockups or marketing assets to demonstrate how our services could improve your business presence.
                </p>
                
                <p><strong>b. Personal Information You Provide</strong></p>
                <p>
                  When you voluntarily submit information through our contact forms, scheduling tools, or email, we may collect:
                </p>
                <ul>
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Company name</li>
                  <li>Service interests or project descriptions</li>
                </ul>
                
                <p><strong>c. Automatically Collected Data</strong></p>
                <p>
                  When you visit our website, we may automatically collect certain data such as:
                </p>
                <ul>
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Referring URLs</li>
                  <li>Time spent on pages</li>
                  <li>Cookies or tracking pixels (see Section 7)</li>
                </ul>
                
                <h2>3. How We Use Your Information</h2>
                <p>
                  We use the information we collect to:
                </p>
                <ul>
                  <li>Create and present customized demo websites or marketing campaigns</li>
                  <li>Reach out with relevant offers based on publicly available business needs</li>
                  <li>Manage communication, onboarding, and service delivery</li>
                  <li>Improve our services, marketing, and user experience</li>
                  <li>Operate automations (email, SMS, CRM) through tools like Go High Level</li>
                </ul>
                <p>
                  We may use automated workflows and AI-driven processes to create content, manage customer relationships, and deliver personalized experiences based on your business data.
                </p>
                
                <h2>4. Third-Party Tools and Services</h2>
                <p>
                  We integrate with and use the following platforms:
                </p>
                <ul>
                  <li>Emergent.sh – to generate visual website mockups</li>
                  <li>Go High Level (GHL) – to manage client communications, automations, and CRM</li>
                  <li>Zapier – to connect and automate workflows between platforms</li>
                  <li>Google Analytics / Meta Pixel – for analytics and retargeting (if applicable)</li>
                </ul>
                <p>
                  These tools may collect or process data in accordance with their respective privacy policies. We do not control how third-party platforms use your data once transferred. However, we strive to only use reputable providers with robust data handling practices.
                </p>
                
                <h2>5. Legal Basis for Processing (for EU/EEA Residents)</h2>
                <p>
                  If you are located in the European Economic Area, our legal basis for collecting and using your personal data depends on the context, but generally includes:
                </p>
                <ul>
                  <li>Legitimate interests (e.g., using publicly available data to demonstrate our services)</li>
                  <li>Your consent (when submitting forms or opting into communication)</li>
                  <li>Contractual necessity (to fulfill service agreements)</li>
                </ul>
                
                <h2>6. Opt-Out & Data Removal Requests</h2>
                <p>
                  If your business was featured in a demo or you were contacted as part of our outreach efforts and would prefer not to be included:
                </p>
                <ul>
                  <li>You may request removal of any demo materials</li>
                  <li>You may opt out of any future contact</li>
                </ul>
                <p>
                  To do so, email us at hello@snaplaunchstudio.com and we will remove your data promptly.
                </p>
                
                <h2>7. Cookies & Tracking</h2>
                <p>
                  Our website may use cookies or similar technologies for analytics and performance optimization. These help us:
                </p>
                <ul>
                  <li>Understand website traffic and usage behavior</li>
                  <li>Improve user experience and marketing efforts</li>
                </ul>
                <p>
                  You can disable cookies through your browser settings.
                </p>
                
                <h2>8. Data Security</h2>
                <p>
                  We take reasonable precautions to secure data from unauthorized access, disclosure, alteration, or destruction. This includes secure data transmission, access controls, and limiting retention of scraped or demo-related data.
                </p>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                </p>
                
                <h2>9. Children's Privacy</h2>
                <p>
                  Our services are not intended for children under the age of 13. We do not knowingly collect personal information from minors.
                </p>
                
                <h2>10. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy periodically. Changes will be posted on this page with a revised "Effective Date." Continued use of our services after any changes constitutes acceptance of the new terms.
                </p>
                
                <h2>11. Contact Us</h2>
                <p>
                  For questions, opt-out requests, or data concerns, contact:
                </p>
                <p>
                  Snap Launch Studio<br />
                  Email: <a href="mailto:hello@snaplaunchstudio.com">hello@snaplaunchstudio.com</a><br />
                  Website: <a href="https://www.snaplaunchstudio.com">https://www.snaplaunchstudio.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPage;
